import { Injectable } from '@angular/core';
import { Observable, of, delay, tap } from 'rxjs';
import { StaffService } from './staff.service';
import { Staff } from '../../interfaces/institution/staff/staff.interface';
import { StaffSearchParam } from '../../interfaces/institution/staff/staff-search-param.interface';
import { PageResponse } from '../../../../core/interfaces/page-response.interface';

@Injectable({
    providedIn: 'root'
})
export class StaffMockService extends StaffService {

    private mockStaff: Staff[] = [
        {
            uniqueId: '1',
            fullName: 'Ravi Kumar',
            email: 'ravi@psgtech.ac.in',
            phoneNo: '+91 98765 43210',
            status: 'ACTIVE',
            joinedDate: 'Jan 26',
            institutionUniqueId: 'inst-123'
        },
        {
            uniqueId: '2',
            fullName: 'Priya Sharma',
            email: 'priya@psgtech.ac.in',
            phoneNo: '+91 87654 32109',
            status: 'PENDING',
            joinedDate: 'Feb 10',
            institutionUniqueId: 'inst-123'
        },
        {
            uniqueId: '3',
            fullName: 'Arvind Swamy',
            email: 'arvind@psgtech.ac.in',
            phoneNo: '+91 76543 21098',
            status: 'INVITED',
            joinedDate: 'Mar 15',
            institutionUniqueId: 'inst-456'
        },
        {
            uniqueId: '4',
            fullName: 'Geeta Menon',
            email: 'geeta@psgtech.ac.in',
            phoneNo: '+91 65432 10987',
            status: 'INACTIVE',
            joinedDate: 'Apr 02',
            institutionUniqueId: 'inst-789'
        }
    ];

    constructor() {
        super();
    }

    override findAll(searchParam: StaffSearchParam): Observable<PageResponse<Staff>> {
        let filteredStaff = [...this.mockStaff];

        if (searchParam.institutionUniqueId) {
            filteredStaff = filteredStaff.filter(s => s.institutionUniqueId === searchParam.institutionUniqueId);
        }

        if (searchParam.searchTerm) {
            const term = searchParam.searchTerm.toLowerCase();
            filteredStaff = filteredStaff.filter(s =>
                s.fullName.toLowerCase().includes(term) ||
                s.email.toLowerCase().includes(term)
            );
        }

        return this.wrapRequest(
            of({
                content: filteredStaff,
                page: {
                    number: 0,
                    size: searchParam.size || 10,
                    totalElements: filteredStaff.length,
                    totalPages: Math.ceil(filteredStaff.length / (searchParam.size || 10))
                }
            }).pipe(delay(1000)),
            false,
            'Failed to fetch staff members'
        );
    }

    override find(id: string): Observable<Staff> {
        const staff = this.mockStaff.find(s => s.uniqueId === id);
        return this.wrapRequest(
            of(staff!).pipe(delay(1000)),
            false,
            'Failed to fetch staff member'
        );
    }

    override create(data: Partial<Staff>): Observable<Staff> {
        const newStaff = { ...data, id: Math.random().toString(36).substring(7), joinedDate: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) } as Staff;
        this.mockStaff.push(newStaff);

        return this.wrapRequest(
            of(newStaff).pipe(delay(1500)).pipe(tap(() => {
                this.toast.success('Staff member created successfully');
            })),
            true,
            'Failed to create staff member'
        );
    }

    override update(id: string, data: Partial<Staff>): Observable<Staff> {
        const index = this.mockStaff.findIndex(s => s.uniqueId === id);
        if (index !== -1) {
            this.mockStaff[index] = { ...this.mockStaff[index], ...data } as Staff;
        }
        return this.wrapRequest(
            of(this.mockStaff[index]).pipe(delay(1500)).pipe(tap(() => {
                this.toast.success('Staff member updated successfully');
            })),
            true,
            'Failed to update staff member'
        );
    }

    override delete(id: string): Observable<void> {
        this.mockStaff = this.mockStaff.filter(s => s.uniqueId !== id);
        return this.wrapRequest(
            of(undefined).pipe(delay(1000)).pipe(tap(() => {
                this.toast.success('Staff member deleted successfully');
            })),
            true,
            'Failed to delete staff member'
        );
    }

    override inviteStaff(staff: Partial<Staff>[]): Observable<any> {
        const newStaffMembers = staff.map(s => ({
            ...s,
            id: Math.random().toString(36).substring(7),
            status: 'PENDING',
            joinedDate: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
        } as Staff));

        this.mockStaff = [...newStaffMembers, ...this.mockStaff];

        return this.wrapRequest(
            of({ success: true }).pipe(delay(1500)).pipe(tap(() => {
                this.toast.success(`${staff.length} staff invitations sent successfully`);
            })),
            true,
            'Failed to send invitations'
        );
    }

    override uploadStaffCSV(institutionId: string, file: File): Observable<Staff[]> {
        // Mock parsing CSV
        const mockStaff: Staff[] = [
            {
                uniqueId: 'csv-1',
                fullName: 'CSV Staff One',
                email: 'staff1@csv.com',
                phoneNo: '1234567890',
                status: 'PENDING',
                joinedDate: 'Jan 20',
                institutionUniqueId: institutionId
            },
            {
                uniqueId: 'csv-2',
                fullName: 'CSV Staff Two',
                email: 'staff2@csv.com',
                phoneNo: '0987654321',
                status: 'PENDING',
                joinedDate: 'Jan 21',
                institutionUniqueId: institutionId
            }
        ];

        return this.wrapRequest(
            of(mockStaff).pipe(delay(1500)).pipe(tap(() => {
                this.toast.success('CSV uploaded and processed successfully');
            })),
            false,
            'Failed to process CSV'
        );
    }

    override updateStatus(id: string, status: boolean): Observable<void> {
        const index = this.mockStaff.findIndex(s => s.uniqueId === id);
        if (index !== -1) {
            this.mockStaff[index].status = status ? 'ACTIVE' : 'INACTIVE';
        }
        return this.wrapRequest(
            of(undefined).pipe(delay(1000)).pipe(tap(() => {
                this.toast.success('Staff status updated successfully');
            })),
            false,
            'Failed to update staff status'
        );
    }

    override updatePassword(uniqueId: string, password: string): Observable<void> {
        return this.wrapRequest(
            of(undefined).pipe(delay(1000)).pipe(tap(() => {
                this.toast.success('Staff password updated successfully');
            })),
            false,
            'Failed to update staff password'
        );
    }
}
